import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import ProductCard from "@/components/public/ProductCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const search = params.get("search") || "";
  const [q, setQ] = useState(search);

  useEffect(() => {
    api.get("/public/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/public/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (brand) qs.set("brand", brand);
    if (search) qs.set("search", search);
    api.get(`/public/products?${qs.toString()}`).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, brand, search]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    setParams(p);
  };
  const submitSearch = (e) => { e.preventDefault(); setParam("search", q); };

  const symbol = settings?.currency?.symbol || "Rs.";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="products-sidebar">
            <form onSubmit={submitSearch} className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9" data-testid="products-search" />
            </form>

            <div className="mt-6" data-testid="brand-filters">
              <p className="overline mb-3 text-primary">Brands</p>
              <div className="flex flex-wrap gap-2 lg:flex-col lg:items-start">
                <button onClick={() => setParam("brand", "")} data-testid="brand-all"
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors lg:w-full ${!brand ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>All Brands</button>
                {brands.map((b) => (
                  <button key={b.id} onClick={() => setParam("brand", b.name)} data-testid={`brand-filter-${b.slug || b.name}`}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors lg:w-full ${brand.toLowerCase() === b.name.toLowerCase() ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
                    {b.logo
                      ? <img src={b.logo} alt="" className="h-5 w-5 shrink-0 rounded-full object-contain" />
                      : <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary">{(b.name || "?")[0]}</span>}
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main */}
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight lg:text-4xl">Products</h1>
            <p className="mt-1 text-muted-foreground">Explore products on easy installments.</p>

            {category && (() => {
              const cat = categories.find((c) => c.slug === category);
              return cat ? (
                <div className="relative mt-6 overflow-hidden rounded-2xl border border-border" data-testid="category-banner">
                  <img src={cat.image} alt={cat.name} className="h-40 w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-center p-8 text-white">
                    <h2 className="font-display text-3xl font-extrabold">{cat.name}</h2>
                    <p className="text-sm text-white/80">Available on easy installments</p>
                  </div>
                </div>
              ) : null;
            })()}

            <div className="mt-6 flex flex-wrap gap-2" data-testid="category-filters">
              <button onClick={() => setParam("category", "")} className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${!category ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>All</button>
              {categories.map((c) => (
                <button key={c.id} onClick={() => setParam("category", c.slug)} data-testid={`filter-${c.slug}`}
                  className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${category === c.slug ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>{c.name}</button>
              ))}
            </div>

            {loading ? (
              <p className="mt-16 text-center text-muted-foreground">Loading...</p>
            ) : products.length === 0 ? (
              <p className="mt-16 text-center text-muted-foreground" data-testid="no-products">No products found.</p>
            ) : (
              <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
                {products.map((p) => <ProductCard key={p.id} p={p} symbol={symbol} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
